import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Plus, Trash2, Edit2, Send, ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

// ---- Types ----

interface Webhook {
  id: string;
  name: string;
  targetUrl: string;
  eventPattern: string;
  retryMaxAttempts: number;
  enabled: boolean;
  createdAt: string;
}

interface WebhookDelivery {
  id: string;
  webhookId: string;
  eventPattern: string;
  status: string;
  attempt: number;
  lastError: string | null;
  nextRetryAt: string | null;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-900/40 text-yellow-300',
  DELIVERING: 'bg-blue-900/40 text-blue-300',
  SUCCESS: 'bg-green-900/40 text-green-300',
  FAILED: 'bg-red-900/40 text-red-300',
  DEAD_LETTER: 'bg-muted text-foreground',
};

/**
 * Story 82.4 — /admin/webhooks
 *
 * Lists webhooks with create/edit/delete. Each row has:
 *   - "Send test event" button
 *   - Expandable deliveries panel (last 50 attempts) with per-row "Retry from DLQ"
 */
export function WebhooksPage() {
  const { t } = useTranslation();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Webhook | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiClient
      .get<Webhook[]>('/webhooks')
      .then(({ data }) => setWebhooks(data))
      .catch(() => setWebhooks([]))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  function handleEdit(wh: Webhook) {
    setEditing(wh);
    setFormOpen(true);
  }

  async function handleDelete(id: string) {
    if (!confirm(t('webhooks.deleteConfirm'))) return;
    try {
      await apiClient.delete(`/webhooks/${id}`);
      setRefreshToken((x) => x + 1);
    } catch {
      setError(t('webhooks.error_delete'));
    }
  }

  async function handleTest(id: string) {
    try {
      await apiClient.post(`/webhooks/${id}/test`);
    } catch {
      setError(t('webhooks.error_test'));
    }
  }

  function handleSaved() {
    setFormOpen(false);
    setEditing(null);
    setRefreshToken((x) => x + 1);
  }

  if (loading) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('webhooks.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('webhooks.subtitle')}</p>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">{error}</div>
      )}

      <div className="flex justify-end">
        <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t('webhooks.create')}
        </Button>
      </div>

      {formOpen && (
        <WebhookForm
          initial={editing}
          onSaved={handleSaved}
          onCancel={() => { setFormOpen(false); setEditing(null); }}
        />
      )}

      {webhooks.length === 0 && !formOpen ? (
        <div className="py-12 text-center text-sm text-muted-foreground">{t('webhooks.empty')}</div>
      ) : (
        <div className="space-y-2">
          {webhooks.map((wh) => (
            <WebhookRow
              key={wh.id}
              webhook={wh}
              expanded={expandedId === wh.id}
              onToggleExpand={() => setExpandedId(expandedId === wh.id ? null : wh.id)}
              onEdit={() => handleEdit(wh)}
              onDelete={() => handleDelete(wh.id)}
              onTest={() => handleTest(wh.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Webhook row with deliveries panel ----

function WebhookRow({
  webhook,
  expanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onTest,
}: {
  webhook: Webhook;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onTest: () => void;
}) {
  const { t } = useTranslation();
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [dlLoading, setDlLoading] = useState(false);
  const [dlRefresh, setDlRefresh] = useState(0);

  useEffect(() => {
    if (!expanded) return;
    setDlLoading(true);
    apiClient
      .get<WebhookDelivery[]>(`/webhooks/${webhook.id}/deliveries`)
      .then(({ data }) => setDeliveries(data))
      .catch(() => setDeliveries([]))
      .finally(() => setDlLoading(false));
  }, [expanded, webhook.id, dlRefresh]);

  async function handleResurrect(deliveryId: string) {
    try {
      await apiClient.post(`/webhooks/deliveries/${deliveryId}/resurrect`);
      setDlRefresh((x) => x + 1);
    } catch {
      // silent — no toast available here
    }
  }

  return (
    <Card className="overflow-hidden">
      <div className="flex items-start gap-4 p-4">
        <button onClick={onToggleExpand} className="mt-0.5 shrink-0 text-muted-foreground">
          {expanded
            ? <ChevronDown className="h-4 w-4" />
            : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{webhook.name}</span>
            <span className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-semibold',
              webhook.enabled ? 'bg-green-900/40 text-green-300' : 'bg-muted text-muted-foreground',
            )}>
              {webhook.enabled ? 'ON' : 'OFF'}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="truncate max-w-xs">{webhook.targetUrl}</span>
            <span>{t('webhooks.eventPattern')}: <code className="font-mono">{webhook.eventPattern}</code></span>
            <span>{t('webhooks.retryMaxAttempts')}: {webhook.retryMaxAttempts}</span>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="ghost" title={t('webhooks.sendTest')} onClick={onTest}>
            <Send className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* Deliveries panel */}
      {expanded && (
        <div className="border-t border-border bg-muted/30 px-4 py-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('webhooks.deliveries.title')}
          </div>
          {dlLoading ? (
            <div className="text-xs text-muted-foreground">{t('common.loading')}</div>
          ) : deliveries.length === 0 ? (
            <div className="text-xs text-muted-foreground">{t('webhooks.deliveries.empty')}</div>
          ) : (
            <div className="space-y-1">
              {deliveries.map((d) => (
                <div key={d.id} className="flex items-center gap-3 rounded-md bg-background px-3 py-2 text-xs">
                  <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold', STATUS_COLORS[d.status] ?? 'bg-muted text-muted-foreground')}>
                    {t(`webhooks.deliveries.statuses.${d.status}`, { defaultValue: d.status })}
                  </span>
                  <span className="text-muted-foreground">#{d.attempt}</span>
                  {d.lastError && <span className="truncate text-destructive">{d.lastError}</span>}
                  <span className="ml-auto shrink-0 text-muted-foreground">
                    {new Date(d.createdAt).toLocaleString()}
                  </span>
                  {d.status === 'DEAD_LETTER' && (
                    <Button size="sm" variant="ghost" onClick={() => handleResurrect(d.id)}>
                      <RotateCcw className="h-3.5 w-3.5" />
                      <span className="ml-1">{t('webhooks.deliveries.resurrect')}</span>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ---- Webhook form ----

function WebhookForm({
  initial,
  onSaved,
  onCancel,
}: {
  initial: Webhook | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? '');
  const [targetUrl, setTargetUrl] = useState(initial?.targetUrl ?? '');
  const [eventPattern, setEventPattern] = useState(initial?.eventPattern ?? 'lead.created');
  const [retryMaxAttempts, setRetryMaxAttempts] = useState(initial?.retryMaxAttempts ?? 5);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const EVENT_PATTERNS = [
    'lead.created',
    'opportunity.stage_changed',
    'opportunity.closed_won',
    'opportunity.stale',
  ];

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = { name, targetUrl, eventPattern, retryMaxAttempts, enabled };
    try {
      if (initial) {
        await apiClient.put(`/webhooks/${initial.id}`, payload);
      } else {
        await apiClient.post('/webhooks', payload);
      }
      onSaved();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('webhooks.error_save'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="mb-4 font-medium">
        {initial ? t('webhooks.edit') : t('webhooks.create')}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium">{t('webhooks.name')}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">{t('webhooks.targetUrl')}</label>
            <Input value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="https://..." required />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium">{t('webhooks.eventPattern')}</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={eventPattern}
              onChange={(e) => setEventPattern(e.target.value)}
            >
              {EVENT_PATTERNS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">{t('webhooks.retryMaxAttempts')}</label>
            <Input
              type="number"
              min={1}
              max={10}
              value={retryMaxAttempts}
              onChange={(e) => setRetryMaxAttempts(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="wh-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          <label htmlFor="wh-enabled" className="text-sm">{t('webhooks.enabled')}</label>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button type="submit" disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
}
